/*
 * *********  Private Helper functions **********
 */

import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import unionBy from 'lodash/unionBy';
import moment from 'moment';
import {
    ImeasurementPoint,
    ImeasurementPointAnswer,
    ImeasurementPointList,
    ImeasurementPointListTab,
    ImeasurementPointResult
} from '../models';
import {
    measurementPointPassFailTypes,
    measurementPointResultStatusTypesEnum,
    measurementPointTypesEnum,
    MPLTypebyJobTypesID,
    measurementPointListTypeEnum
} from '../models-enums';
import { IinitialState } from '../reducers';
import { initialMeasurmentPointResult } from '../reducers/initialState';
import {
    selectJobDefaultForMeasurementPoint,
    selectMeasurementPointListForInstallBase,
    selectMeasurementPointsForTabID
} from '../reducers/measurementPointListsReducer';
import {
    selectMPLRsForJobAndMPL,
    selectMostRecentResult,
    selectMostRecentTemporaryResult
} from '../reducers/measurementPointResultsReducer';
import { v4 as uuidv4 } from 'uuid';
/*
 ************   EXPORTS
 */

/*
 * when no results for the specific install, try to find results for the same measurementPointList
 */
export const prepareMeasurementPointListResults = (results: ImeasurementPointResult[]) => {
    return map(results, result => {
        return {
            ...result,
            measurementPointAnswers: map(result.measurementPointAnswers, answer => {
                if (Object.keys(answer).findIndex(key => key === 'numericValue') !== -1) {
                    return {
                        ...answer,
                        numericValue: Number(answer?.numericValue) ?? null
                    };
                }
                return {
                    ...answer
                };
            }),
            temporary: false,
            updateDate: moment.utc().toISOString()
        };
    });
};

/*
 *  filter MPs that are deleted, belong to a different customer, or are not required (Pass Fails are automatically required but do not have isRequired = true)
 */
export const filterMPsToValidate = (measurementPoints: { [key: string]: ImeasurementPoint }) => {
    return filter(
        measurementPoints,
        point =>
            point.isDeleted === false &&
            point.type !== measurementPointTypesEnum.group &&
            (point.isRequired === true || point.type === measurementPointTypesEnum.passFail)
    );
};

/*
 * pre-fill answers with defaults
 */
export const createDefaultAnswers = (
    measurementPointTabs: ImeasurementPointListTab[],
    getState: () => IinitialState
) => {
    let defaultMeasurementPointAnswers: ImeasurementPointAnswer[] = [];
    measurementPointTabs.forEach(tab => {
        const measurementPoints = selectMeasurementPointsForTabID(getState(), {
            tabID: tab.id
        });
        Object.values(measurementPoints).forEach(point => {
            const jobDefaultForPoint = selectJobDefaultForMeasurementPoint(getState(), { measurementPoint: point });
            const { passFail, select, numeric } = measurementPointTypesEnum;

            switch (point.type) {
                case numeric: {
                    if (jobDefaultForPoint) {
                        const numericAnswer: ImeasurementPointAnswer = {
                            measurementPointID: point.id,
                            showInReport: jobDefaultForPoint.defaultMpAnswer.showInReport
                        };
                        if (jobDefaultForPoint.defaultMpAnswer.numericValue !== undefined) {
                            numericAnswer.numericValue = jobDefaultForPoint.defaultMpAnswer.numericValue;
                        }
                        defaultMeasurementPointAnswers = [...defaultMeasurementPointAnswers, numericAnswer];
                    }
                    break;
                }
                case passFail: {
                    const passFailAnswer: ImeasurementPointAnswer = {
                        measurementPointID: point.id,
                        showInReport: point.showInReport
                    };
                    if (point.passFailDefault !== undefined) {
                        passFailAnswer.pass = point.passFailDefault;
                    }
                    defaultMeasurementPointAnswers = [...defaultMeasurementPointAnswers, passFailAnswer];
                    break;
                }
                case select: {
                    const selectAnswer: ImeasurementPointAnswer = {
                        measurementPointID: point.id,
                        showInReport: point.showInReport
                    };
                    if (point.selectDefaultOptionID !== undefined) {
                        selectAnswer.measurementPointSelectOptionID = point.selectDefaultOptionID;
                    }
                    defaultMeasurementPointAnswers = [...defaultMeasurementPointAnswers, selectAnswer];
                    break;
                }
                default:
            }
        });
    });
    return defaultMeasurementPointAnswers;
};

/** A maintenance-trigger control (vs a normal numeric/passFail/select/etc. measurement point). */
export const isMaintenanceMP = (mp: ImeasurementPoint | undefined): boolean =>
    mp?.type === measurementPointTypesEnum.maintenance;

/*
 * Maintenance MPs bypass the form, so they aren't re-emitted each save. Keep each save complete by
 * carrying untouched maintenance answers forward. (New jobs start clean - see createNewResultFromPrevious.)
 */
export const mergeMaintenanceAnswers = (
    formAnswers: ImeasurementPointAnswer[],
    existingResultAnswers: ImeasurementPointAnswer[],
    stagedMaintenanceAnswers: ImeasurementPointAnswer[],
    measurementPointsByID: { [key: string]: ImeasurementPoint }
): ImeasurementPointAnswer[] => {
    const stagedMpIDs = new Set(stagedMaintenanceAnswers.map(m => m.measurementPointID));
    const carriedMaintenanceAnswers = (existingResultAnswers || []).filter(
        a => isMaintenanceMP(measurementPointsByID[a.measurementPointID]) && !stagedMpIDs.has(a.measurementPointID)
    );
    return [
        ...formAnswers.filter(a => !stagedMpIDs.has(a.measurementPointID)),
        ...carriedMaintenanceAnswers,
        ...stagedMaintenanceAnswers
    ];
};

const getMostRecentResult = (results: ImeasurementPointResult[]) => {
    return results.reduce((previous, current) => {
        if (moment.utc(previous.updateDate).isAfter(moment.utc(current.updateDate))) {
            return previous;
        } else {
            return current;
        }
    });
};

const resetAnswerToDefault = (
    measurementPoint: ImeasurementPoint,
    answer: ImeasurementPointAnswer
): ImeasurementPointAnswer => {
    if (measurementPoint.type === measurementPointTypesEnum.passFail) {
        const result: ImeasurementPointAnswer = { ...answer, notes: '' };
        if (measurementPoint.passFailDefault !== undefined) {
            result.pass = measurementPoint.passFailDefault;
        }
        return result;
    } else if (measurementPoint.type === measurementPointTypesEnum.numeric) {
        return { ...answer, notes: '' };
    } else if (measurementPoint.type === measurementPointTypesEnum.date) {
        return { ...answer, notes: '' };
    } else if (measurementPoint.type === measurementPointTypesEnum.text) {
        return { ...answer, notes: '' };
    } else if (measurementPoint.type === measurementPointTypesEnum.select) {
        const result: ImeasurementPointAnswer = { ...answer, notes: '' };
        if (measurementPoint.selectDefaultOptionID !== undefined) {
            result.measurementPointSelectOptionID = measurementPoint.selectDefaultOptionID;
        }
        return result;
    } else {
        return { ...answer, notes: '' };
    }
};

/*
 * Loop over the answers we received from a previous result from a similar device:
 *  clear notes
 *  for answers that we should not remember, clear or reset them to the default. (Pass/Fail and Selects have a default)
 * Don't remember if:
 ** rememberBetweenDevice = false - remember between installs that have the same MPL
 ** rememberBetweenInspection = false - remember between jobs
 * remove the Result Notes (comments)
 */
const cleanExistingResultAnswers = (
    result: ImeasurementPointResult,
    measurementPointsByID: { [key: string]: ImeasurementPoint },
    jobID: string,
    installBaseID: string
): ImeasurementPointResult => {
    const cleanedAnswers = result.measurementPointAnswers.map(answer => {
        const measurementPoint = measurementPointsByID[answer.measurementPointID];
        if (!measurementPoint) {
            console.error('[cleanExistingResultAnswers]: missing measurement point', measurementPoint, answer);
            return { ...answer, notes: '' };
        }

        if (
            measurementPoint.selectRememberBetweenDevice === undefined ||
            measurementPoint.selectRememberBetweenInspection === undefined
        ) {
            // reset answer to the default
            return resetAnswerToDefault(measurementPoint, answer);
        }

        if (
            measurementPoint.selectRememberBetweenDevice === true &&
            measurementPoint.selectRememberBetweenInspection === true
        ) {
            return { ...answer, notes: '' };
        } else if (
            measurementPoint.selectRememberBetweenDevice === false &&
            measurementPoint.selectRememberBetweenInspection === true &&
            installBaseID === result.installBaseID
        ) {
            return { ...answer, notes: '' };
        } else if (
            measurementPoint.selectRememberBetweenDevice === true &&
            measurementPoint.selectRememberBetweenInspection === false &&
            jobID === result.jobID
        ) {
            return { ...answer, notes: '' };
        } else {
            // reset answer to the default
            return resetAnswerToDefault(measurementPoint, answer);
        }
    });
    return {
        ...result,
        measurementPointAnswers: cleanedAnswers,
        notes: '',
        status: 0
    };
};

/*
 * cleanExistingInstallResultAnswers - loop over answers for this device and clean any answers that are from a different job if rememberBetweenInspections === false
 */
const cleanExistingInstallResultAnswers = (
    result: ImeasurementPointResult,
    measurementPointsByID: { [key: string]: ImeasurementPoint },
    jobID: string
): ImeasurementPointResult => {
    // if it is temporary then it has already been cleaned
    if (result.temporary) {
        return result;
    }
    const cleanedAnswers = result.measurementPointAnswers.map(answer => {
        const measurementPoint = measurementPointsByID[answer.measurementPointID];
        if (!measurementPoint) {
            console.error('[cleanExistingInstallResultAnswers]: missing measurement point', measurementPoint, answer);
            return { ...answer, notes: '' };
        }

        if (jobID !== result.jobID && measurementPoint.selectRememberBetweenInspection === false) {
            return resetAnswerToDefault(measurementPoint, answer);
        } else {
            return answer;
        }
    });
    return {
        ...result,
        measurementPointAnswers: cleanedAnswers,
        notes: '',
        status: 0,
        previousStatus: result.status
    };
};

/*
 * getPreviousResultFromSameCategory
 * find a previous result for the same MPL and part of this job
 * this allows users to inspect one asset and then all similar assets will
 * default to these values
 */
const getPreviousResultFromSameCategory = (
    mpResults: ImeasurementPointResult[],
    installID: string,
    selectedMPL: ImeasurementPointList,
    measurementPointsByID: { [key: string]: ImeasurementPoint },
    jobID: string,
    state: IinitialState
): ImeasurementPointResult => {
    if (mpResults.length) {
        let previousResult: ImeasurementPointResult = initialMeasurmentPointResult;
        const MPLresults = selectMPLRsForJobAndMPL(state, {
            MPListID: selectedMPL.id
        });

        if (MPLresults.length) {
            const mostRecentMPLresult = getMostRecentResult(MPLresults);

            previousResult = cleanExistingResultAnswers(mostRecentMPLresult, measurementPointsByID, jobID, installID);
        }

        return previousResult;
    } else {
        return initialMeasurmentPointResult;
    }
};

/*
 * updateResultAnswerIDs
 * When creating new result, it cannot have the same answer IDs as a previous result
 */
const updateResultAnswerIDs = (resultAnswers: ImeasurementPointAnswer[]) => {
    return resultAnswers.map(answer => ({ ...answer, id: uuidv4() }));
};

/*
 * Copy a previous result to a new one
 */
const createNewResultFromPrevious = (
    previousResult: ImeasurementPointResult,
    jobID: string,
    jobTypeID: string,
    installBaseID: string,
    measurementPointTabs: ImeasurementPointListTab[],
    getState: () => IinitialState
): ImeasurementPointResult => {
    const existingAnswersWithNewIDs = updateResultAnswerIDs(previousResult.measurementPointAnswers) || [];
    const { measurementPointsByID } = getState().measurementPointLists;
    // Maintenance answers are per-job (owned by the trigger table). Seeding from a different job drops
    // them so the editor falls back to the trigger; a same-job reopen keeps them.
    const isCrossJobSeed = previousResult.jobID !== jobID;
    const existingAnswersWithValues = filter(existingAnswersWithNewIDs, answer => {
        if (isCrossJobSeed && isMaintenanceMP(measurementPointsByID[answer.measurementPointID])) {
            return false;
        }
        const hasNumeric = answer.numericValue !== undefined;
        const hasPass = answer.pass !== undefined;
        const hasDate = answer.dateValue !== undefined;
        const hasText = answer.textValue !== undefined;
        const hasOption = answer.measurementPointSelectOptionID !== undefined;

        return hasNumeric || hasPass || hasDate || hasText || hasOption;
    });
    const newAnswersWithIDs = updateResultAnswerIDs(createDefaultAnswers(measurementPointTabs, getState));

    // combine the answers, prefering existing ones with values
    // this helps when a MPL has been edited and a new question has been added
    const measurementPointAnswers = unionBy(existingAnswersWithValues, newAnswersWithIDs, 'measurementPointID');
    const result: ImeasurementPointResult = {
        id: uuidv4(),
        installBaseID,
        measurementPointListID: previousResult.measurementPointListID,
        jobID,
        jobTypeID,
        temporary: true,
        notes: previousResult.notes ? previousResult.notes : '',
        status: measurementPointResultStatusTypesEnum.resultStatusNotTested,
        createDate: moment.utc().toISOString(),
        updateDate: moment.utc().toISOString(),
        manualOverride: false,
        isDeleted: false,
        measurementPointAnswers,
        DamageCode: null
    };
    if (previousResult.previousStatus !== undefined) {
        result.previousStatus = previousResult.previousStatus;
    }
    return result;
};

/*
 *  create a completely new measurement point list result
 *  pre-fill answers with defaults
 */
const createNewResult = (
    jobID: string,
    installBaseID: string,
    measurementPointListID: string,
    measurementPointTabs: ImeasurementPointListTab[],
    getState: () => IinitialState
): ImeasurementPointResult => {
    const newAnswersWithIDs = updateResultAnswerIDs(createDefaultAnswers(measurementPointTabs, getState));

    return {
        ...initialMeasurmentPointResult,
        id: uuidv4(),
        installBaseID,
        measurementPointListID,
        jobID,
        temporary: true,
        createDate: moment.utc().toISOString(),
        updateDate: moment.utc().toISOString(),
        measurementPointAnswers: newAnswersWithIDs
    };
};

/*
 * If this is a pass fail, check if it is set to pass or if the defaultSelection is pass
 * If this is another type of measurement point, then simply check if it has an answer.
 */
const validateAnswer = (measurementPoint: ImeasurementPoint, answers: ImeasurementPointAnswer[]) => {
    let validError = '';
    const foundAnswer = answers.find(answer => answer.measurementPointID === measurementPoint.id);
    if (foundAnswer) {
        const { passFail, numeric, select, text, date, longText } = measurementPointTypesEnum;

        if (measurementPoint.type === passFail && foundAnswer.pass === measurementPointPassFailTypes.fail) {
            validError = `${measurementPoint.label} on this device failed previously.  Manual inspection required`;
        } else if (measurementPoint.isRequired) {
            if (measurementPoint.type === numeric && foundAnswer.numericValue === undefined) {
                validError = `${measurementPoint.label}: answer required.`;
            } else if (measurementPoint.type === text && foundAnswer.textValue === undefined) {
                validError = `${measurementPoint.label}: answer required.`;
            } else if (measurementPoint.type === select && foundAnswer.measurementPointSelectOptionID === undefined) {
                validError = `${measurementPoint.label}: answer required.`;
            } else if (measurementPoint.type === date && foundAnswer.dateValue === undefined) {
                validError = `${measurementPoint.label}: answer required.`;
            } else if (measurementPoint.type === longText && foundAnswer.textValue === undefined) {
                validError = `${measurementPoint.label}: answer required.`;
            }
        }
    } else {
        validError = `${measurementPoint.label}: answer required.`;
    }

    return validError;
};

/*
 * validateNewResult
 * Validate new result for passSelected()
 * loop over the measurement point tabs then the measurement points
 */
export const validateNewResult = (
    measurementPointList: ImeasurementPointList,
    newResult: ImeasurementPointResult,
    getState: () => IinitialState
) => {
    let resultErrorMessages: string[] = [];
    // if the previous result was a fail, then it is not valid for copying to the other devices in the room
    if (newResult.status === measurementPointResultStatusTypesEnum.resultStatusFail) {
        return ['most recent result for this category, '];
    }

    measurementPointList.measurementPointTabs.forEach(tab => {
        const MPsToValidate = filterMPsToValidate(
            selectMeasurementPointsForTabID(getState(), { tabID: tab.id })
            // customerID
        );
        MPsToValidate.forEach(point => {
            const resultErrors = validateAnswer(point, newResult.measurementPointAnswers);
            if (resultErrors.length) {
                resultErrorMessages = [
                    ...resultErrorMessages,
                    validateAnswer(point, newResult.measurementPointAnswers)
                ];
            }
        });
    });

    return resultErrorMessages;
};

/*
 * initInstallBaseResult
 * receives an installBaseID and creates a temporary result
 */
export const initInstallBaseResult = (getState: () => IinitialState, { installID }: { installID: string }) => {
    const {
        manageJob: { selectedJob },
        facilities: { facilitiesByID },
        manageInventory: { installBasesByID },
        measurementPointResults: { measurementPointResultsByID },
        measurementPointLists: { measurementPointsByID }
    } = getState();
    const facility = facilitiesByID[selectedJob.facilityID];
    const standardID = facility?.standardID;
    const installBase = installBasesByID[installID];

    if (!installBase) {
        console.error('Install base not found:', installID);
        return initialMeasurmentPointResult;
    }

    const filteredMPR = Object.values(measurementPointResultsByID).filter(result => result.jobID === selectedJob.id);
    const filteredMPRByID = keyBy(filteredMPR, 'id');
    const temporaryPreviousResultForInstall = selectMostRecentTemporaryResult(
        filteredMPRByID,
        installID,
        selectedJob.id,
        true
    );
    const selectedMeasurementPointList = selectMeasurementPointListForInstallBase(getState(), {
        materialID: installBase.materialID,
        standardID: standardID || '',
        shouldShowWarning: false
    });

    if (temporaryPreviousResultForInstall && temporaryPreviousResultForInstall.id.length) {
        return temporaryPreviousResultForInstall;
    } else {
        const previousResultForInstall = selectMostRecentResult(
            filteredMPRByID,
            installID,
            selectedMeasurementPointList.id
        );
        if (previousResultForInstall && previousResultForInstall.id.length) {
            return createNewResultFromPrevious(
                cleanExistingInstallResultAnswers(previousResultForInstall, measurementPointsByID, selectedJob.id),
                selectedJob.id,
                selectedJob.jobTypeID,
                installID,
                selectedMeasurementPointList.measurementPointTabs,
                getState
            );
        }
    }

    const previousResultFromSameCategory = getPreviousResultFromSameCategory(
        filteredMPR,
        installID,
        selectedMeasurementPointList,
        measurementPointsByID,
        selectedJob.id,
        getState()
    );
    const { installBaseID: previousInstallBaseId } = previousResultFromSameCategory;
    // unless current installBaseId isn't checked against the previous, this will go through and pull the previous results even if they are different.
    const fromPrevious = [
        !isEmpty(previousResultFromSameCategory),
        previousResultFromSameCategory.id.length,
        installID === previousInstallBaseId
    ].every(Boolean);

    if (fromPrevious) {
        return createNewResultFromPrevious(
            previousResultFromSameCategory,
            selectedJob.id,
            selectedJob.jobTypeID,
            installID,
            selectedMeasurementPointList.measurementPointTabs,
            getState
        );
    } else {
        return createNewResult(
            selectedJob.id,
            installID,
            selectedMeasurementPointList.id,
            selectedMeasurementPointList.measurementPointTabs,
            getState
        );
    }
};

/*
 * validateJobLevelMeasurementPoints
 * Validates that all REQUIRED job-level measurement points have answers
 * Checks CONFIGURATION first (doesn't assume SimpleMeasurementPointListResult exists)
 */
export const validateJobLevelMeasurementPoints = (getState: () => IinitialState): {
    valid: boolean;
    missingMP?: {
        label: string;
        tabID: string;
        mpID: string;
        listType: string;
        tabName: string;
    };
} => {
    const {
        manageJob: { selectedJob },
        facilities: { facilitiesByID },
        measurementPointLists: { measurementPointListsByID, measurementPointsByID },
        simpleMeasurementPointResults: { simpleMeasurementPointResultsByID }
    } = getState();

    const facility = facilitiesByID[selectedJob.facilityID];
    if (!facility) return { valid: true };

    // Job-level MP main category ID
    const jobMainCategoryID = 'ee4e20f7-2541-414e-8810-dd53dc3064ea';
    
    // Map job type to measurement point list type
    const mpListType = MPLTypebyJobTypesID[selectedJob.jobTypeID] as unknown as measurementPointListTypeEnum | undefined;
    if (mpListType === undefined || mpListType === measurementPointListTypeEnum.none) {
        return { valid: true };
    }

    // Find configured job-level measurement point lists
    const configuredJobLevelLists = Object.values(measurementPointListsByID).filter(
        list =>
            list.standardID === facility.standardID &&
            list.type === mpListType &&
            list.mainCategoryID === jobMainCategoryID &&
            !list.isDeleted
    );

    if (configuredJobLevelLists.length === 0) {
        return { valid: true };
    }

    // Get all job-level tabs (from lists that have Job category)
    const allJobLevelTabs: ImeasurementPointListTab[] = [];
    configuredJobLevelLists.forEach(list => {
        if (list.measurementPointTabs) {
            const jobLevelTabs = list.measurementPointTabs.filter(tab => !tab.isDeleted);
            allJobLevelTabs.push(...jobLevelTabs);
        }
    });

    if (allJobLevelTabs.length === 0) {
        return { valid: true };
    }

    // Check each required MP for answers
    for (const tab of allJobLevelTabs) {
        const requiredMPs = Object.values(measurementPointsByID).filter(
            mp => mp.measurementPointTabID === tab.id && mp.isRequired && !mp.isDeleted
        );

        for (const mp of requiredMPs) {
            // Check if answer exists in SimpleMeasurementPointListResults
            let hasAnswer = false;
            
            for (const result of Object.values(simpleMeasurementPointResultsByID)) {
                if (result.jobID !== selectedJob.id) continue;
                if (!result.measurementPointAnswers) continue;

                const answer = result.measurementPointAnswers.find(a => a.measurementPointID === mp.id);
                if (answer && hasValidAnswer(mp, answer)) {
                    hasAnswer = true;
                    break;
                }
            }

            if (!hasAnswer) {
                return {
                    valid: false,
                    missingMP: {
                        label: mp.label,
                        tabID: tab.id,
                        mpID: mp.id,
                        listType: String(mpListType),
                        tabName: tab.name
                    }
                };
            }
        }
    }

    return { valid: true };
};

// Helper function
function hasValidAnswer(mp: ImeasurementPoint, answer: ImeasurementPointAnswer): boolean {
    switch (mp.type) {
        case measurementPointTypesEnum.passFail:
            return answer.pass !== null && answer.pass !== undefined;
        case measurementPointTypesEnum.numeric:
            return answer.numericValue !== null && answer.numericValue !== undefined;
        case measurementPointTypesEnum.text:
        case measurementPointTypesEnum.longText:
            return !!answer.textValue;
        case measurementPointTypesEnum.select:
            return !!answer.measurementPointSelectOptionID;
        case measurementPointTypesEnum.date:
            return !!answer.dateValue;
        default:
            return true;
    }
}
